import React, { useCallback, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { faArrowRotateRight, faPersonDigging } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import ActionItemGroupsList from "~/actionItems/actionItemGroup/ActionItemGroupsList";
import { ActionItemsTabContext } from "~/actionItems/ActionItemsTab";
import { GROUP_BY_CUSTOM, Views } from "~/actionItems/constants";
import Filters from "~/actionItems/filters/Filters";
import ErrorBoundary from "~/common/ErrorBoundary";
import { GatewayTimeoutError, type GraphQLError } from "~/types/errors";
import { Button, LoadingSpinner } from "~/ui";
import { Link, Page } from "~/ui/legacyFellowIndex";

import SyntheticActionItemGroupsList from "./actionItemGroup/SyntheticActionItemGroupsList";
import ArchiveActionItemsBanner from "./ArchiveActionItemsBanner";
import { ActionItemsContext, ActionItemsState } from "./newActionItems/ActionItemsContext";

import styles from "./ActionItems.module.css";

export type GroupsState = { [key: string]: { collapsed: boolean; expansions: number } };

function CreateActionItemButtonPage() {
	const { onCreateActionItem } = useContext(ActionItemsContext);

	const onClickCreateActionItem = useCallback(() => onCreateActionItem(), [onCreateActionItem]);

	return (
		<Button variant="primary" size="normal" onClick={onClickCreateActionItem}>
			Create action item
		</Button>
	);
}

function SelectItemsViewPage() {
	const { onTeammatesView, onUserView, view } = useContext(ActionItemsContext);

	return (
		<>
			<Button size="wide" className={cx(styles.tab, { [styles.activeTab]: view === Views.USER })} onClick={onUserView}>
				Assigned to me
			</Button>
			<Button
				size="wide"
				className={cx(styles.tab, { [styles.activeTab]: view === Views.TEAMMATES })}
				onClick={onTeammatesView}
			>
				Assigned to others
			</Button>
		</>
	);
}

/*
 * Fallback component rendered in the case where ActionItemGroupsList components
 * fail to load due to a request timeout.
 */
function ActionItemsGroupsTimedOutMessage({ retry }: { retry: () => void }) {
	return (
		<div className={styles.actionItemGroupsTimeoutContainer}>
			<FontAwesomeIcon icon={faPersonDigging} />
			<h1>Oops! Failed to load...</h1>
			<p>There might be too many action items. Our team is aware of this limit and is working on a fix.</p>
			<Link className={styles.retryLink} onClick={retry} aria-label="retry">
				<FontAwesomeIcon icon={faArrowRotateRight} />
				Retry
			</Link>
		</div>
	);
}

/*
 * Wrapper that displays action item groups based on filters.
 *
 * While data is being loaded, a loading spinner is displayed.
 *
 * If the query fails due to a timeout, a "timed out" / "couldn't load"
 * fallback is displayed, otherwise we propagate the error upstream.
 */
export function ActionItemsList() {
	const { filters } = useContext(ActionItemsContext);
	const { groupBy } = filters;
	const [refetchCount, setRefetchCount] = useState<number>();

	const errorBoundaryFallback = useCallback(({ error, retry }: { error: Error; retry: () => void }) => {
		const isGatewayTimeout = error instanceof GatewayTimeoutError;
		const isTimeout =
			isGatewayTimeout || (error as GraphQLError).source?.errors?.some((err: { timeout: boolean }) => err.timeout);

		// If the error is not a fetch timeout, then we
		// let the upstream ErrorBoundary handle it.
		if (!isTimeout) throw error;

		const retryFetch = function () {
			setRefetchCount(previous => (previous ?? 0) + 1);
			retry();
		};

		// eslint-disable-next-line
		return <ActionItemsGroupsTimedOutMessage retry={retryFetch} />;
	}, []);

	const refetchProps = refetchCount
		? {
				fetchKey: `refetch-${refetchCount}`,
			}
		: {};

	return (
		<ErrorBoundary fallbackComponent={errorBoundaryFallback}>
			<React.Suspense fallback={<LoadingSpinner dimmer={false} />}>
				{groupBy === GROUP_BY_CUSTOM ? <ActionItemGroupsList /> : <SyntheticActionItemGroupsList {...refetchProps} />}
			</React.Suspense>
		</ErrorBoundary>
	);
}

export default function ActionItemsPage() {
	const { streamId } = useContext(ActionItemsTabContext);
	const { view: routeView } = useParams<{ view: string }>();
	const initialView = useMemo(() => {
		if (streamId) return Views.STREAM_TAB;
		return routeView && routeView.toLowerCase() === "teammates" ? Views.TEAMMATES : Views.USER;
	}, [routeView, streamId]);

	const [view] = useState<Views>(initialView);

	if ([Views.STREAM_TAB].includes(view)) {
		return (
			<ActionItemsState>
				<Filters />
				<ActionItemsList />
			</ActionItemsState>
		);
	}

	return (
		<ActionItemsState>
			<Page title="Action items">
				<Page.Header className={styles.header} sticky>
					<div>
						<Page.Title>Action Items</Page.Title>
						<Page.HeaderActions>
							<CreateActionItemButtonPage />
						</Page.HeaderActions>
					</div>
					<Page.HeaderActions className={styles.tabs}>
						<SelectItemsViewPage />
					</Page.HeaderActions>
				</Page.Header>
				<Page.Body>
					<Filters />
					<ArchiveActionItemsBanner />
					<ActionItemsList />
				</Page.Body>
			</Page>
		</ActionItemsState>
	);
}
